import type { SupplierInfoDto } from "@semo/api-contracts";

/**
 * 직접 입력 로그인에 쓰면 안 되는 공급사 ID — 실제 업체 계정이다.
 *
 * 2026-10-06 GS 검수(V-02): 로그인 화면이 이 업체들의 카드(대표자·담당자·업종)를 시험원에게
 * 나열했고, 같은 ID 로 직접 입력 로그인을 하면 그 업체 정보가 서버(씨마켓 동기화 = 덮어쓰기)에서
 * 바뀔 수 있었다. 카드와 실제 업체 정보는 번들에서 뺐고, ID 만 남겨 막는 데 쓴다.
 */
export const RESERVED_SUPPLIER_IDS: ReadonlySet<string> = new Set([
  "samho",
  "youngwoo",
  "seoro",
  "hongmi",
  "kwon-0414",
  "yushingagu",
  "garam5566",
  "dorybusic",
  "yoonil5353",
]);

export const isReservedSupplierId = (supplierId: string): boolean =>
  RESERVED_SUPPLIER_IDS.has(supplierId.trim().toLowerCase());

/** 32비트 FNV-1a — 같은 문자열이면 언제나 같은 씨앗. */
const fnv1a = (text: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

/** mulberry32 — 씨앗이 같으면 같은 수열. */
const seededRandom = (seed: number) => {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** 휴대폰 번호를 010-0000-0000 꼴로. 형식이 아니면 null. */
export const normalizeMobilePhone = (raw: string): string | null => {
  const digits = raw.replace(/\D/g, "");
  if (!/^01[016789]\d{7,8}$/.test(digits)) return null;
  return digits.length === 10
    ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
    : `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

/**
 * 직접 입력 로그인이 보내는 회사 정보.
 *
 * 종전에는 누를 때마다 사업자번호·전화·주소·계좌를 **새 난수**로 만들어 보냈고, 서버는 씨마켓
 * 동기화 규칙(받은 값이 정본)대로 저장값을 덮어썼다 — 같은 ID 로 다시 들어가기만 해도 회사
 * 정보·계약서 공급자 칸이 바뀌었다(V-02, A3-13). 이제는 **공급사 ID 를 씨앗으로** 값을 만든다.
 * 같은 ID·같은 회사명·같은 휴대폰이면 보내는 값이 늘 같아서 서버가 «변경 없음» 으로 건너뛴다.
 *
 * 직인 주소는 보내지 않는다(null) — 예전 고정 주소는 열리지 않는 씨마켓 페이지라 견적서에서
 * 그림이 깨졌다. 비워 보내면 서버는 저장된 직인을 그대로 둔다.
 */
export const buildManualSupplierInfo = (input: {
  supplierId: string;
  companyName: string;
  managerPhone?: string | null;
}): SupplierInfoDto => {
  const supplierId = input.supplierId.trim();
  const companyName = input.companyName.trim();
  const next = seededRandom(fnv1a(supplierId));
  const digits = (length: number) =>
    Array.from({ length }, () => Math.floor(next() * 10)).join("");

  const businessNumber = `${digits(3)}-${digits(2)}-${digits(5)}`;
  const streetNumber = String(1 + Math.floor(next() * 899));
  const zipCode = digits(5);
  const companyPhone = `02-${digits(4)}-${digits(4)}`;
  const companyFax = `02-${digits(4)}-${digits(4)}`;
  const fallbackMobile = `010-${digits(4)}-${digits(4)}`;
  const bankAccountNumber = digits(14);
  const typedMobile = input.managerPhone?.trim() ?? "";

  return {
    supplierId,
    companyName,
    businessNumber,
    ceoName: "김대표",
    addressFull: `서울시 강남구 테헤란로 ${streetNumber}`,
    zipCode,
    businessType: "도소매",
    productDescription: "종합물류",
    companyPhone,
    managerName: "박담당",
    contactEmail: `contact@${supplierId}.com`,
    managerDepartment: "영업팀",
    managerPosition: "과장",
    companyFax,
    managerPhone: typedMobile !== "" ? typedMobile : fallbackMobile,
    bankName: "신한은행",
    bankAccountNumber,
    bankAccountHolder: companyName,
    companySealUrl: null,
  };
};
